import { Pause, Play } from "@phosphor-icons/react";
import { promisify } from "es6-promisify";
import ky from "ky";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import WaveformData from "waveform-data";

import { useColors } from "~/hooks/colors";

import Waveform from "./Waveform";

export const AudioClue: React.FC<
  Required<Pick<React.ComponentProps<"audio">, "src" | "autoPlay">>
> = ({ src, autoPlay }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const requestRef = useRef<number>(null);

  const [isPlaying, setIsPlaying] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [waveformData, setWaveformData] = useState<WaveformData | null>(null);

  useEffect(() => {
    const animate = () => {
      if (isPlaying && audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
        requestRef.current = requestAnimationFrame(animate);
      }
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }

    audioRef.current.addEventListener("play", () => {
      setIsPlaying(true);
    });

    audioRef.current.addEventListener("pause", () => {
      setIsPlaying(false);
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    ky(src, { signal: controller.signal })
      .arrayBuffer()
      .then(async (arrayBuffer) => {
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const waveformData = await promisify(WaveformData.createFromAudio)({
          audio_buffer: audioBuffer,
          disable_worker: true,
        });

        setWaveformData(waveformData);
      });
    return () => {
      controller.abort();
    };
  }, [src]);

  const Icon = useMemo(() => (isPlaying ? Pause : Play), [isPlaying]);
  const colors = useColors();

  return (
    <>
      <audio
        hidden
        src={src}
        autoPlay={autoPlay}
        ref={audioRef}
        onCanPlayThrough={(event) => {
          setDuration(event.currentTarget.duration);
        }}
      />
      {waveformData && (
        <div className="absolute inset-x-0 inset-y-1/6 flex">
          <div className="relative grow">
            <div
              className="absolute inset-0 flex justify-end overflow-hidden"
              style={{
                left: duration ? `${(currentTime / duration) * 100}%` : 0,
              }}
            >
              <Waveform data={waveformData} color={colors["base-200"]} />
            </div>
            <div
              className="absolute inset-0 overflow-hidden"
              style={{
                width: duration ? `${(currentTime / duration) * 100}%` : 0,
              }}
            >
              <Waveform data={waveformData} color={colors["base-300"]} />
            </div>
          </div>
        </div>
      )}
      <button
        disabled={duration === null}
        className="btn btn-circle btn-ghost btn-lg relative"
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={async (event) => {
          event.stopPropagation();

          if (audioRef.current?.paused) {
            await audioRef.current.play();
          } else {
            audioRef.current?.pause();
          }
        }}
      >
        <Icon className="size-10" weight="fill" />
      </button>
    </>
  );
};
